import { useEffect, useState } from "react";
import { Instagram, Loader2, Save, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useApi } from "@/hooks/useApi";
import { PageHeader, LoadingGrid, ErrorState, Avatar, StatusBadge, UploadButton, Field, StatCard } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { PolicyLinks } from "@/components/Footer";
import { api, errMsg } from "@/lib/api";
import { compact, fdate } from "@/lib/format";

const EDITABLE = ["full_name", "phone", "location", "bio", "instagram_username", "instagram_url", "audience_info", "profile_picture"];

function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", f);
      toast.success("Password changed");
      setF({ current_password: "", new_password: "" });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="uc-card p-6 space-y-4">
      <p className="font-display text-lg flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" />Change password</p>
      <Field label="Current password"><Input type="password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className="rounded-xl bg-background" data-testid="current-password-input" /></Field>
      <Field label="New password"><Input type="password" minLength={8} required value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="rounded-xl bg-background" data-testid="new-password-input" /></Field>
      <Button disabled={busy} variant="outline" className="rounded-full" data-testid="change-password-button">Update password</Button>
    </form>
  );
}

export default function CreatorProfile() {
  const { data, loading, error, reload } = useApi("/me/profile");
  const { refresh } = useAuth();
  const { branding } = useBranding();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setF(Object.fromEntries(EDITABLE.map((k) => [k, data[k] || ""]))); }, [data]);
  if (loading || !f) return error ? <ErrorState message={error} onRetry={reload} /> : <LoadingGrid count={3} className="space-y-4" h="h-32" />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/me/profile", f);
      toast.success("Profile updated");
      reload(); refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ic = "rounded-xl bg-background h-11";
  return (
    <div>
      <PageHeader eyebrow={`Managed by ${branding.website_name}`} title="Your profile" />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <div className="uc-card p-6 text-center" data-testid="profile-card">
            <Avatar src={f.profile_picture} name={f.full_name} className="h-24 w-24 mx-auto" />
            <div className="mt-4"><UploadButton label="Change photo" onUploaded={(r) => setF({ ...f, profile_picture: r.url })} testid="profile-picture-upload" /></div>
            <p className="font-display text-xl mt-4">{data.full_name}</p>
            <a href={data.instagram_url} target="_blank" rel="noreferrer" className="text-sm text-muted-foreground flex items-center justify-center gap-1.5 mt-1 hover:text-primary"><Instagram className="h-4 w-4" />@{data.instagram_username}</a>
            <div className="flex justify-center gap-2 mt-3">
              <span className="rounded-full border border-border px-2.5 py-0.5 text-xs" data-testid="profile-category">{data.category_name}</span>
              <StatusBadge status={data.status} testid="profile-status" />
            </div>
            <p className="text-xs text-muted-foreground mt-4">{data.email} · Joined {fdate(data.joined_at)}</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Followers" value={compact(data.followers)} testid="profile-followers" />
            <StatCard label="Avg views" value={compact(data.avg_reel_views)} testid="profile-views" />
            <StatCard label="Eng." value={`${Number(data.engagement || 0).toFixed(1)}%`} testid="profile-engagement" />
          </div>
          <p className="text-[11px] text-muted-foreground">Performance stats and category are maintained by the {branding.website_name} team.</p>
        </div>
        <div className="lg:col-span-2 space-y-6">
          <div className="uc-card p-6 space-y-4" data-testid="profile-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name"><Input value={f.full_name} onChange={set("full_name")} className={ic} data-testid="profile-name-input" /></Field>
              <Field label="Phone / WhatsApp"><Input value={f.phone} onChange={set("phone")} className={ic} data-testid="profile-phone-input" /></Field>
              <Field label="Instagram username"><Input value={f.instagram_username} onChange={set("instagram_username")} className={ic} data-testid="profile-instagram-username-input" /></Field>
              <Field label="Instagram profile URL"><Input value={f.instagram_url} onChange={set("instagram_url")} className={ic} data-testid="profile-instagram-url-input" /></Field>
              <Field label="Location"><Input value={f.location} onChange={set("location")} className={ic} data-testid="profile-location-input" /></Field>
            </div>
            <Field label="Audience information" hint="e.g. age range, top cities, gender split"><Textarea value={f.audience_info} onChange={set("audience_info")} className="rounded-xl bg-background" data-testid="profile-audience-input" /></Field>
            <Field label="Bio"><Textarea value={f.bio} onChange={set("bio")} className="rounded-xl bg-background" data-testid="profile-bio-input" /></Field>
            <Button onClick={save} disabled={busy} className="rounded-full" data-testid="profile-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />Save changes</>}</Button>
          </div>
          <ChangePassword />
          <PolicyLinks className="text-xs text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}
